/**
 * The browser-side half of `/lead-venue` — the two gates a lead's
 * `place_guess` goes through before it is allowed to become a venue.
 *
 * `localVenueMatch` is the free check, done against venues already loaded on
 * screen (the same fold `frame.ts` and `leadVenue.ts` use, kept in step by
 * hand rather than imported — this file is bundled into the browser and the
 * Worker's `leadVenue.ts` imports `@anthropic-ai/sdk`, which has no business
 * in that bundle). `checkLeadVenue` and `addLeadVenue` are the two actions
 * the Worker route answers, called only once the free check has already
 * found nothing.
 */
import { supabase } from './supabase'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

export interface VenueDraft {
  /** Left out on a hand-typed draft — the Worker derives it from `name`. */
  id?: string
  name: string
  kind: string | null
  street: string | null
  postcode: string | null
  city: string | null
  district: string | null
  site_url: string | null
  /** Null on a draft Doug typed himself rather than one a web search
   *  verified. Both are real: the newest and smallest Berlin venues are
   *  sometimes in no directory and no search result at all, and his own
   *  knowledge of the place is the only source there is. */
  source_url: string | null
}

export type LeadVenueCheck =
  | { status: 'duplicate'; venue: { id: string; name: string } }
  | { status: 'verified'; draft: VenueDraft }
  | { status: 'not_found'; reason: string }

/** Same containment rule `titleSimilarity` in the Worker's `src/store.ts`
 *  uses for the same question — duplicated rather than imported for the
 *  same bundling reason `fold` above is: this file ships to the browser
 *  and `store.ts`, with its Worker-only imports, does not. */
function normTitle(s: string): string {
  return s
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function titleSimilarity(a: string, b: string): number {
  const words = (v: string) => new Set(normTitle(v).split(' ').filter((w) => w.length > 2))
  const x = words(a)
  const y = words(b)
  if (!x.size || !y.size) return 0
  let shared = 0
  for (const w of x) if (y.has(w)) shared++
  const smaller = Math.min(x.size, y.size)
  if (shared < 2 && smaller > 1) return 0
  return shared / smaller
}

/** Looser than `DEDUPE_THRESHOLD` (0.55) for a venue name: an event title
 *  has more room to differ in wording, and this only ever prints a hint for
 *  Doug to glance at, never a decision made for him. Word overlap alone
 *  misses a cross-language duplicate entirely — a German newsletter's
 *  "Symphonie der Farben" against a harvested "Symphony of Colors" shares
 *  only "Paul Signac" — which is a real gap, not a bug this threshold can
 *  fix; catching the same-language case is still worth having. */
const DUPLICATE_TITLE_THRESHOLD = 0.5

export interface VenueEventLite { id: string; title: string; run_start: string | null; run_end: string | null }

/**
 * Is this lead's event already on the list at the venue it named? Free,
 * client-side, the same shape as `localVenueMatch` above but asking about
 * the event rather than the place — the question behind "why does a lead
 * for a venue I already have still sit here needing a decision": most of
 * the time it is not that the venue is missing, it is that Doug has not
 * yet told the app the newsletter's mention and the recipe's own harvest
 * are the same night.
 */
export function possibleDuplicate(
  lead: { title: string | null; dates: string[] },
  venueEvents: VenueEventLite[],
): VenueEventLite | null {
  if (!lead.title) return null
  let best: VenueEventLite | null = null
  let bestScore = 0
  for (const e of venueEvents) {
    const score = titleSimilarity(lead.title, e.title)
    if (score < DUPLICATE_TITLE_THRESHOLD) continue
    // A date the lead gave should fall inside the entry's own run, where
    // either side actually states one. No dates on either side to compare
    // still leaves the title match worth a hint.
    const overlaps = !lead.dates.length || !e.run_start ||
      lead.dates.some((d) => d >= e.run_start! && d <= (e.run_end ?? e.run_start!))
    if (!overlaps) continue
    if (score > bestScore) { bestScore = score; best = e }
  }
  return best
}

/** Same fold `frame.ts`'s `foldForNames` uses, so "Deutsches Theatre" (an
 *  English newsletter's spelling) still folds onto "Deutsches Theater". */
function fold(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/theatre/g, 'theater')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Which known venue a lead's place names, if any — free, and deliberately
 * conservative: exact containment of the folded name or an alias, no fuzzy
 * fallback. A promoted lead filed at the wrong venue is worse than one filed
 * at none, because the place drives the geography and the room rules.
 *
 * The web-search check (`checkLeadVenue`) is the fuzzier, paid second look
 * for exactly the names this one is too strict to catch — it exists so this
 * one can stay strict.
 */
export function localVenueMatch(
  guess: string | null,
  venues: { id: string; name: string; aliases: string[] | null }[],
): { id: string; name: string } | null {
  if (!guess) return null
  const hay = ` ${fold(guess)} `
  for (const v of venues) {
    for (const name of [v.name, ...(v.aliases ?? [])]) {
      if (name && name.length >= 6 && hay.includes(` ${fold(name)} `)) return { id: v.id, name: v.name }
    }
  }
  return null
}

async function authedFetch(body: unknown): Promise<Response> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not signed in')

  return fetch(`${WORKER_URL}/lead-venue`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/** The paid check: is `name` already on the list under a spelling the free
 *  fold missed, and if not, does an independent web source say it is real. */
export async function checkLeadVenue(name: string): Promise<LeadVenueCheck> {
  const res = await authedFetch({ action: 'check', name })
  if (!res.ok) throw new Error(`Venue check failed: ${res.status} ${await res.text()}`)
  return res.json()
}

/** The one write. Only ever call this with a draft `checkLeadVenue` itself
 *  returned as `verified` — the route does not re-verify a draft it is
 *  handed, so making that draft up client-side would defeat the whole gate. */
export async function addLeadVenue(draft: VenueDraft): Promise<{ id: string; name: string }> {
  const res = await authedFetch({ action: 'add', draft })
  if (!res.ok) throw new Error(`Venue add failed: ${res.status} ${await res.text()}`)
  const { venue } = await res.json()
  return venue
}
